import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Download, Plus, Stamp, Trash2 } from 'lucide-react';
import { AppBar, ChipRow, Screen } from '@/components/layout';
import {
  Button,
  Card,
  Chip,
  EmptyState,
  ErrorNote,
  Field,
  Input,
  SectionTitle,
  Spinner,
  Textarea,
} from '@/components/ui';
import { ApiError, api, apiData } from '@/lib/api';
import { formatDate, todayISO } from '@/lib/format';
import type { AdminBillingDocument, AdminFacility, AdminPlan, AdminSupplierInfo } from '@/types';
import { BILLING_TYPE_LABELS, formatWon } from './lib';

/** 정산 발행 관리 (§5.21) — 견적서·거래명세서 발행, 요금제, 공급자 정보 */
export function AdminBillingPage() {
  const [searchParams] = useSearchParams();
  const presetFacilityId = searchParams.get('facilityId') ?? '';

  return (
    <Screen>
      <AppBar title="정산 관리" back />
      <div className="flex flex-col gap-6 p-4">
        <IssueSection presetFacilityId={presetFacilityId} />
        <DocumentsSection />
        <PlansSection />
        <SupplierSection />
      </div>
    </Screen>
  );
}

// ── 새 문서 발행 ─────────────────────────────────────────────

interface ItemRow {
  name: string;
  quantity: string;
  unitPrice: string;
}

function IssueSection({ presetFacilityId }: { presetFacilityId: string }) {
  const queryClient = useQueryClient();
  const [facilityId, setFacilityId] = useState(presetFacilityId);
  const [type, setType] = useState<'QUOTE' | 'STATEMENT'>('QUOTE');
  const [issuedOn, setIssuedOn] = useState(todayISO());
  const [note, setNote] = useState('');
  const [items, setItems] = useState<ItemRow[]>([
    { name: '새싹돋보기 연간 이용료', quantity: '1', unitPrice: '' },
  ]);
  const [feedback, setFeedback] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (presetFacilityId) setFacilityId(presetFacilityId);
  }, [presetFacilityId]);

  const facilitiesQuery = useQuery({
    queryKey: ['admin', 'facilities', ''],
    queryFn: () => apiData<AdminFacility[]>('/admin/facilities'),
  });
  const facilities = facilitiesQuery.data ?? [];

  const total = items.reduce(
    (sum, item) => sum + (Number(item.quantity) || 0) * (Number(item.unitPrice) || 0),
    0,
  );

  const issue = useMutation({
    mutationFn: () =>
      api(`/admin/facilities/${facilityId}/billing-documents`, {
        method: 'POST',
        body: {
          type,
          issuedOn,
          note: note.trim() || undefined,
          items: items
            .filter((item) => item.name.trim() && Number(item.unitPrice) > 0)
            .map((item) => ({
              name: item.name.trim(),
              quantity: Number(item.quantity) || 1,
              unitPrice: Number(item.unitPrice),
            })),
        },
      }),
    onSuccess: () => {
      setError(null);
      setNote('');
      setFeedback(`${BILLING_TYPE_LABELS[type]}를 발행했어요. PDF가 문서 목록에 준비됩니다.`);
      void queryClient.invalidateQueries({ queryKey: ['admin', 'billing-documents'] });
    },
    onError: (caught) =>
      setError(caught instanceof ApiError ? caught.message : '발행하지 못했어요. 다시 시도해 주세요.'),
  });

  const canIssue =
    Boolean(facilityId) && items.some((item) => item.name.trim() && Number(item.unitPrice) > 0);

  return (
    <section>
      <SectionTitle>새 문서 발행</SectionTitle>
      <Card className="flex flex-col gap-4">
        <div>
          <p className="mb-1.5 text-sm font-medium">기관</p>
          <ChipRow>
            {facilities.map((facility) => (
              <Chip
                key={facility.id}
                tone={facilityId === facility.id ? 'primary' : 'neutral'}
                selected={facilityId === facility.id}
                onClick={() => setFacilityId(facility.id)}
              >
                {facility.name}
              </Chip>
            ))}
          </ChipRow>
        </div>

        <div className="flex items-end gap-4">
          <div>
            <p className="mb-1.5 text-sm font-medium">문서 유형</p>
            <div className="flex gap-2">
              {(['QUOTE', 'STATEMENT'] as const).map((value) => (
                <Chip
                  key={value}
                  tone={type === value ? 'primary' : 'neutral'}
                  selected={type === value}
                  onClick={() => setType(value)}
                >
                  {BILLING_TYPE_LABELS[value]}
                </Chip>
              ))}
            </div>
          </div>
          <div className="flex w-40 shrink-0 flex-col gap-0">
            <Field label="발행일" hint="소급 발행 가능">
              <Input type="date" value={issuedOn} onChange={(e) => setIssuedOn(e.target.value)} />
            </Field>
          </div>
        </div>

        <div className="flex flex-col gap-2">
          <p className="text-sm font-medium">품목</p>
          {items.map((item, index) => (
            <div key={index} className="flex items-center gap-2">
              <Input
                placeholder="품목명"
                value={item.name}
                onChange={(e) => updateItem(setItems, index, { name: e.target.value })}
              />
              <div className="w-16 shrink-0">
                <Input
                  type="number"
                  min={1}
                  placeholder="수량"
                  value={item.quantity}
                  onChange={(e) => updateItem(setItems, index, { quantity: e.target.value })}
                />
              </div>
              <div className="w-32 shrink-0">
                <Input
                  type="number"
                  min={0}
                  placeholder="단가(원)"
                  value={item.unitPrice}
                  onChange={(e) => updateItem(setItems, index, { unitPrice: e.target.value })}
                />
              </div>
              {items.length > 1 && (
                <button
                  type="button"
                  aria-label="품목 삭제"
                  className="tap shrink-0 text-ink-sub"
                  onClick={() => setItems((prev) => prev.filter((_, i) => i !== index))}
                >
                  <Trash2 size={17} />
                </button>
              )}
            </div>
          ))}
          <Button
            size="sm"
            variant="outline"
            icon={<Plus size={15} />}
            onClick={() => setItems((prev) => [...prev, { name: '', quantity: '1', unitPrice: '' }])}
          >
            품목 추가
          </Button>
        </div>

        <Field label="거래메모(특이사항)" hint="문서 PDF에도 그대로 인쇄돼요">
          <Textarea
            rows={2}
            placeholder="예: 7월분 정산 · 원아 3명 기준 · 무통장 입금 확인 후 발행"
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />
        </Field>

        {error && (
          <p role="alert" className="rounded-sm bg-notice/10 px-3 py-2 text-sm text-notice">
            {error}
          </p>
        )}
        {feedback && (
          <p className="rounded-sm bg-primary-soft px-3 py-2 text-sm text-primary-strong">{feedback}</p>
        )}

        <div className="flex items-center justify-between">
          <p className="text-sm text-ink-sub">
            합계 <b className="text-base text-ink">{formatWon(total)}</b>
          </p>
          <Button loading={issue.isPending} disabled={!canIssue} onClick={() => issue.mutate()}>
            발행하기
          </Button>
        </div>
      </Card>
    </section>
  );
}

function updateItem(
  setItems: React.Dispatch<React.SetStateAction<ItemRow[]>>,
  index: number,
  patch: Partial<ItemRow>,
) {
  setItems((prev) => prev.map((item, i) => (i === index ? { ...item, ...patch } : item)));
}

// ── 발행 문서 목록 ───────────────────────────────────────────

function DocumentsSection() {
  const [type, setType] = useState('');
  const { data, isPending, isError, refetch } = useQuery({
    queryKey: ['admin', 'billing-documents', type],
    queryFn: () =>
      apiData<AdminBillingDocument[]>(`/admin/billing-documents${type ? `?type=${type}` : ''}`),
    placeholderData: keepPreviousData,
  });
  const documents = data ?? [];

  async function download(id: string) {
    const { url } = await apiData<{ url: string }>(`/admin/billing-documents/${id}/pdf`);
    window.open(url, '_blank', 'noopener');
  }

  return (
    <section>
      <SectionTitle>발행 문서</SectionTitle>
      <div className="mb-3 flex gap-2">
        {['', 'QUOTE', 'STATEMENT'].map((value) => (
          <Chip
            key={value || 'all'}
            tone={type === value ? 'primary' : 'neutral'}
            selected={type === value}
            onClick={() => setType(value)}
          >
            {value ? BILLING_TYPE_LABELS[value] : '전체'}
          </Chip>
        ))}
      </div>

      {isPending && <Spinner />}
      {isError && <ErrorNote message="문서를 불러오지 못했어요." onRetry={() => void refetch()} />}
      {!isPending && documents.length === 0 && (
        <EmptyState title="발행된 문서가 없어요" body="위에서 견적서나 거래명세서를 발행해 보세요." />
      )}

      <ul className="flex flex-col gap-2">
        {documents.map((document) => (
          <Card key={document.id} as="li" className="flex items-center gap-3">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <Chip tone={document.type === 'QUOTE' ? 'info' : 'primary'}>
                  {BILLING_TYPE_LABELS[document.type]}
                </Chip>
                <p className="truncate font-semibold">{document.facility.name}</p>
              </div>
              <p className="mt-1 text-sm text-ink-sub">
                {document.docNo} · {formatDate(document.issuedOn)} · {formatWon(document.amount)}
                {document.sealApplied ? ' · 직인' : ''}
                {document.verifyCode ? ` · 검증코드 ${document.verifyCode}` : ''}
              </p>
              {document.note && (
                <p className="mt-1 line-clamp-2 text-sm text-ink-sub">메모: {document.note}</p>
              )}
            </div>
            {document.pdfKey && (
              <Button
                size="sm"
                variant="outline"
                icon={<Download size={15} />}
                onClick={() => void download(document.id)}
              >
                PDF
              </Button>
            )}
          </Card>
        ))}
      </ul>
    </section>
  );
}

// ── 요금제 ───────────────────────────────────────────────────

function PlansSection() {
  const queryClient = useQueryClient();
  const { data } = useQuery({
    queryKey: ['admin', 'plans'],
    queryFn: () => apiData<AdminPlan[]>('/admin/plans'),
  });
  const plans = data ?? [];

  return (
    <section>
      <SectionTitle>요금제</SectionTitle>
      <div className="flex flex-col gap-2">
        {plans.map((plan) => (
          <PlanCard
            key={plan.id}
            plan={plan}
            onSaved={() => void queryClient.invalidateQueries({ queryKey: ['admin', 'plans'] })}
          />
        ))}
        {plans.length === 0 && (
          <EmptyState title="요금제가 없어요" body="시드 데이터 또는 API로 요금제를 만들어 주세요." />
        )}
      </div>
    </section>
  );
}

interface TierDraft {
  fromCount: string;
  toCount: string;
  additionalFee: string;
}

function PlanCard({ plan, onSaved }: { plan: AdminPlan; onSaved: () => void }) {
  const [name, setName] = useState(plan.name);
  const [baseFee, setBaseFee] = useState(String(plan.baseFee));
  const [active, setActive] = useState(plan.active);
  // 원아 수 구간별 추가 금액 — 마지막 구간은 끝 인원을 비워 "이상"으로 둔다
  const [tiers, setTiers] = useState<TierDraft[]>(() =>
    (plan.tiers ?? []).map((tier) => ({
      fromCount: String(tier.fromCount),
      toCount: tier.toCount === null ? '' : String(tier.toCount),
      additionalFee: String(tier.additionalFee),
    })),
  );

  const parsedTiers = tiers
    .filter((tier) => tier.fromCount.trim() !== '')
    .map((tier) => ({
      fromCount: Number(tier.fromCount) || 0,
      toCount: tier.toCount.trim() === '' ? null : Number(tier.toCount) || 0,
      additionalFee: Number(tier.additionalFee) || 0,
    }));

  const save = useMutation({
    mutationFn: () =>
      api('/admin/plans', {
        method: 'POST',
        body: {
          code: plan.code,
          name: name.trim(),
          baseFee: Number(baseFee) || 0,
          active,
          tiers: parsedTiers,
        },
      }),
    onSuccess: onSaved,
  });

  const dirty =
    name !== plan.name ||
    Number(baseFee) !== plan.baseFee ||
    active !== plan.active ||
    JSON.stringify(parsedTiers) !== JSON.stringify(plan.tiers ?? []);

  return (
    <Card className="flex flex-col gap-2">
      <div className="flex items-center gap-2">
        <span className="rounded-sm bg-bg px-2 py-0.5 text-[12px] text-ink-sub">{plan.code}</span>
        <Chip tone={active ? 'primary' : 'neutral'} selected={active} onClick={() => setActive(!active)}>
          {active ? '판매 중' : '중단'}
        </Chip>
      </div>
      <div className="flex items-end gap-2">
        {/* gap-0 래퍼 — Field 공통 gap 가드로 mb-3 상쇄, items-end 바닥선 정렬 */}
        <div className="flex min-w-0 flex-1 flex-col gap-0">
          <Field label="이름">
            <Input value={name} onChange={(e) => setName(e.target.value)} />
          </Field>
        </div>
        <div className="flex w-36 shrink-0 flex-col gap-0">
          <Field label="연 기본료(원)">
            <Input type="number" min={0} value={baseFee} onChange={(e) => setBaseFee(e.target.value)} />
          </Field>
        </div>
        <Button size="sm" className="mb-0.5" loading={save.isPending} disabled={!dirty} onClick={() => save.mutate()}>
          저장
        </Button>
      </div>

      {/* 원아 수 구간별 추가 금액 — 요금 안내 페이지·견적에 그대로 쓰인다 (§5.21) */}
      <div className="rounded-sm bg-bg p-3">
        <div className="mb-2 flex items-center justify-between">
          <p className="text-sm font-semibold text-ink-sub">원아 수에 따른 추가 금액 (연간)</p>
          <Button
            size="sm"
            variant="outline"
            icon={<Plus size={14} />}
            onClick={() =>
              setTiers((prev) => [...prev, { fromCount: '', toCount: '', additionalFee: '' }])
            }
          >
            구간 추가
          </Button>
        </div>

        {tiers.length === 0 && (
          <p className="text-sm text-ink-sub">구간이 없으면 기본료만 안내돼요.</p>
        )}

        <div className="flex flex-col gap-2">
          {tiers.map((tier, index) => (
            <div key={index} className="flex items-center gap-2">
              <Input
                type="number"
                min={1}
                placeholder="시작"
                className="w-20"
                value={tier.fromCount}
                onChange={(e) =>
                  setTiers((prev) =>
                    prev.map((t, i) => (i === index ? { ...t, fromCount: e.target.value } : t)),
                  )
                }
              />
              <span className="text-sm text-ink-sub">~</span>
              <Input
                type="number"
                min={1}
                placeholder="끝(비우면 이상)"
                className="w-28"
                value={tier.toCount}
                onChange={(e) =>
                  setTiers((prev) =>
                    prev.map((t, i) => (i === index ? { ...t, toCount: e.target.value } : t)),
                  )
                }
              />
              <span className="text-sm text-ink-sub">명 · +</span>
              <Input
                type="number"
                min={0}
                step={10000}
                placeholder="추가 금액"
                className="min-w-0 flex-1"
                value={tier.additionalFee}
                onChange={(e) =>
                  setTiers((prev) =>
                    prev.map((t, i) => (i === index ? { ...t, additionalFee: e.target.value } : t)),
                  )
                }
              />
              <span className="text-sm text-ink-sub">원</span>
              <button
                type="button"
                aria-label="구간 삭제"
                className="tap p-1 text-ink-sub"
                onClick={() => setTiers((prev) => prev.filter((_, i) => i !== index))}
              >
                <Trash2 size={15} />
              </button>
            </div>
          ))}
        </div>
      </div>
    </Card>
  );
}

// ── 공급자(사업자) 정보 ─────────────────────────────────────

const SUPPLIER_FIELDS: { key: keyof AdminSupplierInfo; label: string; placeholder?: string }[] = [
  { key: 'name', label: '상호' },
  { key: 'representative', label: '대표자' },
  { key: 'registrationNo', label: '사업자등록번호', placeholder: '000-00-00000' },
  { key: 'address', label: '주소' },
  { key: 'phone', label: '연락처' },
  { key: 'businessType', label: '업태' },
  { key: 'businessItem', label: '종목' },
];

const OFFICER_FIELDS: { key: keyof AdminSupplierInfo; label: string; placeholder?: string }[] = [
  { key: 'privacyOfficerName', label: '책임자 이름' },
  { key: 'privacyOfficerEmail', label: '책임자 이메일', placeholder: 'privacy@saesak.kr' },
  { key: 'privacyOfficerPhone', label: '책임자 연락처', placeholder: '000-0000-0000' },
];

function SupplierSection() {
  const queryClient = useQueryClient();
  const [form, setForm] = useState<AdminSupplierInfo>({});
  const [loaded, setLoaded] = useState(false);
  const [feedback, setFeedback] = useState<string | null>(null);

  const { data } = useQuery({
    queryKey: ['admin', 'platform-settings'],
    queryFn: () => apiData<{ supplierInfo: AdminSupplierInfo }>('/admin/platform-settings'),
  });

  useEffect(() => {
    if (data && !loaded) {
      setForm(data.supplierInfo ?? {});
      setLoaded(true);
    }
  }, [data, loaded]);

  const save = useMutation({
    mutationFn: () =>
      api('/admin/platform-settings', { method: 'PATCH', body: { supplierInfo: form } }),
    onSuccess: () => {
      setFeedback('공급자 정보를 저장했어요. 다음 발행 문서부터 반영됩니다.');
      void queryClient.invalidateQueries({ queryKey: ['admin', 'platform-settings'] });
    },
  });

  return (
    <section>
      <SectionTitle>공급자(사업자) 정보</SectionTitle>
      <Card className="flex flex-col gap-3">
        <p className="text-sm text-ink-sub">
          견적서·거래명세서 PDF의 공급자란에 인쇄되는 정보예요.
        </p>
        {SUPPLIER_FIELDS.map((field) => (
          <Field key={field.key} label={field.label}>
            <Input
              placeholder={field.placeholder}
              value={form[field.key] ?? ''}
              onChange={(e) => setForm((prev) => ({ ...prev, [field.key]: e.target.value }))}
            />
          </Field>
        ))}

        {/* 개인정보 보호책임자 (§14.1-18) — 처리방침 §7과 공개 사이트 정보에 표시된다 */}
        <div className="border-t border-line pt-3">
          <p className="font-semibold">개인정보 보호책임자</p>
          <p className="mt-0.5 text-sm text-ink-sub">
            개인정보 처리방침의 보호책임자란에 그대로 표시돼요.
          </p>
        </div>
        {OFFICER_FIELDS.map((field) => (
          <Field key={field.key} label={field.label}>
            <Input
              placeholder={field.placeholder}
              value={form[field.key] ?? ''}
              onChange={(e) => setForm((prev) => ({ ...prev, [field.key]: e.target.value }))}
            />
          </Field>
        ))}
        {feedback && (
          <p className="rounded-sm bg-primary-soft px-3 py-2 text-sm text-primary-strong">{feedback}</p>
        )}
        <Button loading={save.isPending} onClick={() => save.mutate()}>
          저장
        </Button>

        <SealUploader />
      </Card>
    </section>
  );
}

/** 직인(인장) 업로드 — 이후 발행되는 견적서·거래명세서 PDF의 공급자란에 합성된다 (§5.21) */
function SealUploader() {
  const queryClient = useQueryClient();
  const fileRef = useRef<HTMLInputElement>(null);
  const [message, setMessage] = useState<string | null>(null);

  const { data: seal } = useQuery({
    queryKey: ['admin', 'seal-url'],
    queryFn: () => apiData<{ url: string | null }>('/admin/platform-settings/seal-url'),
  });

  const upload = useMutation({
    mutationFn: async (file: File) => {
      const base64 = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(String(reader.result).split(',')[1] ?? '');
        reader.onerror = () => reject(new Error('read'));
        reader.readAsDataURL(file);
      });
      return apiData<{ url: string | null }>('/admin/platform-settings/seal', {
        method: 'POST',
        body: { imageBase64: base64, mimeType: file.type },
      });
    },
    onSuccess: () => {
      setMessage('직인을 저장했어요. 이후 발행하는 문서부터 합성됩니다.');
      void queryClient.invalidateQueries({ queryKey: ['admin', 'seal-url'] });
    },
    onError: (caught) =>
      setMessage(caught instanceof ApiError ? caught.message : '직인을 올리지 못했어요. PNG/JPG 2MB 이하로 다시 시도해 주세요.'),
  });

  return (
    <div className="border-t border-line pt-3">
      <p className="mb-1.5 text-sm font-medium">직인(인장)</p>
      <div className="flex items-center gap-3">
        {seal?.url ? (
          <img
            src={seal.url}
            alt="등록된 직인"
            className="h-16 w-16 rounded-sm border border-line object-contain bg-white"
          />
        ) : (
          <div className="grid h-16 w-16 place-items-center rounded-sm border border-dashed border-line-strong text-ink-sub">
            <Stamp size={20} />
          </div>
        )}
        <div className="flex flex-col gap-1.5">
          <Button
            size="sm"
            variant="outline"
            loading={upload.isPending}
            onClick={() => fileRef.current?.click()}
          >
            {seal?.url ? '직인 바꾸기' : '직인 올리기'}
          </Button>
          <p className="text-[13px] text-ink-sub">PNG(투명 배경 권장)·JPG, 2MB 이하</p>
        </div>
      </div>
      <input
        ref={fileRef}
        type="file"
        accept="image/png,image/jpeg"
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) upload.mutate(file);
          e.target.value = '';
        }}
      />
      {message && (
        <p className="mt-2 rounded-sm bg-primary-soft px-3 py-2 text-sm text-primary-strong">{message}</p>
      )}
    </div>
  );
}
